import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Flag, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { v2Api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import type { UserProfile } from '@/lib/services/auth';

interface Props {
  scenarioID: string;
  scenarioName?: string;
  machines: string[];
  /** When true, no top margin so the button aligns inline with siblings (e.g. next to Enable TTX). */
  inlineLayout?: boolean;
}

interface ErrorResponse {
  errors?: Record<string, string[] | string> & { non_field_errors?: string[] };
  exception?: string;
  detail?: string;
  message?: string;
  active_run_id?: number;
  active_run_status?: string;
}

function formatStarterDisplayName(user: UserProfile | null): string {
  if (!user) return 'User';
  const full = String((user as UserProfile & { full_name?: string }).full_name ?? '').trim();
  if (full) return full;
  const parts = [user.first_name, user.last_name].map((s) => (s || '').trim()).filter(Boolean);
  if (parts.length) return parts.join(' ');
  return user.email || user.username || 'User';
}

export function CorporateScenarioContent({
  scenarioID,
  scenarioName,
  machines,
  inlineLayout,
}: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [runName, setRunName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<number | null>(null);

  const buildDefaultRunName = useCallback(() => {
    const starter = formatStarterDisplayName(user);
    if (scenarioName?.trim()) {
      return `${starter} — ${scenarioName.trim()}`;
    }
    return starter;
  }, [scenarioName, user]);

  const openStartModal = () => {
    setSubmitError(null);
    setRunName(buildDefaultRunName());
    setIsModalOpen(true);
  };

  const handleStartScenario = async () => {
    const trimmedName = runName.trim();
    if (!trimmedName) {
      setSubmitError('Run name is required.');
      return;
    }
    setSubmitError(null);
    setIsLoading(true);
    try {
      await v2Api.dashboard.scenarios.start<unknown>(scenarioID, { name: trimmedName });
      setIsModalOpen(false);
      navigate('/scenarios/actives');
    } catch (err: unknown) {
      const res = (err as { response?: { data?: ErrorResponse; status?: number } })?.response;
      const data = res?.data;
      if (data?.active_run_id != null) {
        setActiveRunId(data.active_run_id);
        setSubmitError(
          `An active run already exists for this exercise (status: ${data.active_run_status ?? 'active'}). ` +
            'Open Active Scenarios to view it and configure participants.'
        );
      } else {
        const nameErr = data?.errors?.name;
        const nameMsg = Array.isArray(nameErr) ? nameErr[0] : typeof nameErr === 'string' ? nameErr : null;
        setSubmitError(
          nameMsg ??
            data?.detail ??
            data?.message ??
            (err instanceof Error ? err.message : 'Failed to start scenario')
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={inlineLayout ? 'inline-flex items-center' : 'mt-6'}>
      <Button onClick={openStartModal} disabled={isLoading} className="bg-green-600 hover:bg-green-700 text-white" size="default">
        <Flag className="mr-2 h-4 w-4" />
        Start Scenario
      </Button>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Start Scenario</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              You’re just one step away from launching the scenario.
              We’ll create the Active Run now. Configure participants from the Active Scenarios page next.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {submitError && (
              <>
                <p className="text-sm text-destructive">{submitError}</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    const id = activeRunId;
                    if (id != null) navigate(`/scenarios/active/${id}`);
                    else navigate('/scenarios/actives');
                  }}
                  disabled={isLoading}
                  className="w-full"
                >
                  View Active Scenarios
                </Button>
              </>
            )}
            {!submitError && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="exercise-run-name">Run name</Label>
                  <Input
                    id="exercise-run-name"
                    value={runName}
                    onChange={(e) => setRunName(e.target.value)}
                    placeholder="e.g. Jane Doe — Q2 tabletop drill"
                    maxLength={255}
                    disabled={isLoading}
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    Saved on the active exercise run so you can identify who started it. Your account is
                    recorded separately as the starter.
                  </p>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Participants will be configured from{' '}
                  <span className="font-medium text-foreground">Active Scenarios</span> after the run is created.
                </p>
              </>
            )}
          </div>

          <DialogFooter className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsModalOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleStartScenario}
              disabled={isLoading || !runName.trim()}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start Scenario'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
